import { useCallback } from "react"

import { Button } from "@/components/button"
import { ShowWhenTrue } from "@/components/conditionals"
import { InnerCard } from "@/components/inner-card"
import { InnerCardWarning } from "@/components/inner-card-warning"
import { Input } from "@/components/input"
import { DEFAULT_SLIPPAGE, MAX_SLIPPAGE, MIN_SLIPPAGE } from "@/domains/common/config/slippage"
import { cn, escapeRegExp } from "@/domains/library"
import { SettingsSectionLayout } from "@/domains/settings/components/settings-section-layout"

export const SwapSlippageSection: React.FC<{
  slippage: string
  onSlippageChanged: (slippage: string) => void
}> = ({ slippage, onSlippageChanged }) => {
  const handleSlippageChanged = (value: number) => {
    const num = value && !isNaN(value) ? Number(value) : DEFAULT_SLIPPAGE
    const clampedValue = Math.min(Math.max(num, MIN_SLIPPAGE), MAX_SLIPPAGE)
    onSlippageChanged(String(clampedValue))
  }

  const slippageIsOutOfRange = Number(slippage) > MAX_SLIPPAGE || Number(slippage) < MIN_SLIPPAGE
  const handleInputChanged = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const val = e.target.value
      if (val === "" || /^\d*(?:\\[.])?\d*$/.test(escapeRegExp(val))) {
        const split = val.split(".")
        if (split.length > 1 && split[1] && split[1].length > 4) {
          return
        }
        let newSlippageValue: string = val
        if (val.length === 2 && val[0] === "0" && val[1] && val[1] !== ".") {
          newSlippageValue = val[1]
        }
        if (Number(val) < MIN_SLIPPAGE) {
          newSlippageValue = MIN_SLIPPAGE.toString()
        }
        if (Number(val) > MAX_SLIPPAGE) {
          newSlippageValue = MAX_SLIPPAGE.toString()
        }
        onSlippageChanged(newSlippageValue)
      }
    },
    [onSlippageChanged],
  )

  return (
    <SettingsSectionLayout title="Slippage" description="Configure your slippage tolerance">
      <div className="max-w-2xl">
        <InnerCard hasWarning={slippageIsOutOfRange}>
          <div className={cn("items-center justify-between", "flex flex-row")}>
            <div>
              <Input
                id={"slippage"}
                value={slippage}
                className={cn(
                  "grow",
                  "bg-transparent",
                  "text-lg leading-6 shadow-none",
                  "rounded-xl rounded-l-none border-0 border-transparent",
                  "focus-within:border-0 focus-within:ring-0 focus:border-0 focus:ring-0 focus-visible:ring-0 focus-visible:ring-offset-0",
                  "pl-2",
                )}
                onChange={handleInputChanged}
                placeholder={"0.1234"}
                inputMode="decimal"
                pattern="[0-9]*"
              />
            </div>
          </div>
          <ShowWhenTrue when={slippageIsOutOfRange}>
            <InnerCardWarning warning={"Invalid slippage"} />
          </ShowWhenTrue>
          <div className="grid grid-cols-5 gap-2">
            {[0.1, 0.5, 1, 3, 5].map((value) => {
              return (
                <Button
                  key={value.toString()}
                  variant={"percentage"}
                  onClick={() => {
                    handleSlippageChanged(value)
                  }}
                  size="sm"
                  className={cn(Number(slippage) === Number(value) ? "bg-primary" : "")}
                >
                  {`${value.toFixed(1)}%`}
                </Button>
              )
            })}
          </div>
        </InnerCard>
      </div>
    </SettingsSectionLayout>
  )
}
